import { css, StyleSheet } from 'aphrodite';
import DOMPurify from 'dompurify';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { SparkleIcon } from './composerIcons';
import { composerStyles as styles } from './composerStyles';

interface ComposerAiPanelProps {
	label: string;
	thinking: boolean;
	/** Sanitized and rendered as the suggestion preview once ready. */
	suggestionHtml: string | null;
	acceptLabel: string;
	onAccept: () => void;
	onDismiss: () => void;
}

const dotKeyframes = {
	'0%': { opacity: 0.25, transform: 'translateY(0)' },
	'40%': { opacity: 1, transform: 'translateY(-3px)' },
	'80%': { opacity: 0.25, transform: 'translateY(0)' },
	'100%': { opacity: 0.25, transform: 'translateY(0)' },
};

const dotStyles = StyleSheet.create({
	dot: {
		width: 6,
		height: 6,
		borderRadius: '50%',
		background: THEME.colors.text.light,
		animationName: dotKeyframes,
		animationDuration: '1.1s',
		animationIterationCount: 'infinite',
	},
});

/** The AI assist panel: thinking dots while a request runs, then the suggestion with accept/dismiss. */
export const ComposerAiPanel: React.FC<ComposerAiPanelProps> = ({
	label,
	thinking,
	suggestionHtml,
	acceptLabel,
	onAccept,
	onDismiss,
}) => (
	<div className={css(styles.aiPanel)}>
		<div className={css(styles.aiPanelHeader)}>
			<SparkleIcon size={13} />
			<span className={css(styles.aiPanelLabel)}>{label}</span>
		</div>
		{thinking ? (
			<div className={css(styles.aiThinkingDots)}>
				<span className={css(dotStyles.dot)} />
				<span className={css(dotStyles.dot)} style={{ animationDelay: '0.18s' }} />
				<span className={css(dotStyles.dot)} style={{ animationDelay: '0.36s' }} />
			</div>
		) : (
			suggestionHtml && (
				<div className={css(styles.aiPanelBody)}>
					<div
						className={css(styles.aiPreview)}
						dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(suggestionHtml) }}
					/>
					<div className={css(styles.aiPanelActions)}>
						<Button variant='primary' size='sm' styles={[styles.aiAcceptBtn]} onClick={onAccept}>
							{acceptLabel}
						</Button>
						<Button variant='ghost' size='sm' styles={[styles.aiDismissBtn]} onClick={onDismiss}>
							Dismiss
						</Button>
					</div>
				</div>
			)
		)}
	</div>
);
